/**
 * STATUS BADGE COMPONENT
 *
 * Reusable status badge for organizations, tasks, and other entities.
 */

import { cn } from '@/lib/utils';
import type { OrganizationStatus, TaskStatus } from '@/types/database';

interface StatusBadgeProps {
  status: OrganizationStatus | TaskStatus | string;
  className?: string;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  // Organization statuses
  PENDING_REVIEW: {
    label: 'Pending Review',
    className:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  APPROVED: {
    label: 'Approved',
    className:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  AWAITING_PAYMENT: {
    label: 'Awaiting Payment',
    className:
      'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  },
  PAYMENT_RECEIVED: {
    label: 'Payment Received',
    className:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  IT_IN_PROGRESS: {
    label: 'IT In Progress',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  DATA_CONFIGURATION: {
    label: 'Data Configuration',
    className:
      'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  },
  FINAL_REVIEW: {
    label: 'Final Review',
    className:
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  },
  ACTIVATED: {
    label: 'Activated',
    className:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  REJECTED: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  DEACTIVATED: {
    label: 'Deactivated',
    className: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },

  // Task statuses
  PENDING: {
    label: 'Pending',
    className:
      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  BLOCKED: {
    label: 'Blocked',
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  COMPLETED: {
    label: 'Completed',
    className:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  CANCELLED: {
    label: 'Cancelled',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const config = statusConfig[status] || {
    label: status.replace(/_/g, ' '),
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
        config.className,
        className
      )}
    >
      {config.label}
    </span>
  );
}
